import React from 'react';
import PropTypes from 'prop-types';

import Button from '../components/Button';

/**
 * This file contains components and utils to implement an accessible tab panel interface
 * as described here: https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel
 */

/**
 * This function handles keyboard navigation for a tab panel UI.
 * It should be attached to the onKeyDown listener for each tab
 * in order to be able to navigate tabs with arrow keys.
 *
 * @param {Object}  event     The Javascript event.
 * @param {Array}   tabs      The array of tab elements for the tab list.
 * @param {integer} tabIndex  The index of the current tab in the tab list.
 * @param {boolean} vertical  Whether or not the tab list is vertical.
 */
const onKeyDown = (event, tabs, tabIndex, vertical = false) => {

	const { key } = event;

	if (!tabs || tabs.length === 0) {
		return;
	}

	const firstIndex = 0;
	const lastIndex = tabs.length - 1;

	let indexToFocus;

	switch (key) {

	// Focus on next tab (or first)
	case 'ArrowRight':
	case 'ArrowDown':

		if ((!vertical && key === 'ArrowRight') ||
			(vertical && key === 'ArrowDown')) {

			event.preventDefault();

			if (tabIndex === lastIndex) {

				indexToFocus = firstIndex;

			} else {

				indexToFocus = tabIndex + 1;

			}

		}

		break;

	// Focus on previous tab (or last)
	case 'ArrowLeft':
	case 'ArrowUp':

		if ((!vertical && key === 'ArrowLeft') ||
		(vertical && key === 'ArrowUp')) {

			event.preventDefault();

			if (tabIndex === firstIndex) {

				indexToFocus = lastIndex;

			} else {

				indexToFocus = tabIndex - 1;

			}

		}

		break;


	// Jump to the first tab
	case 'Home':
		event.preventDefault();
		indexToFocus = firstIndex;
		break;

	// Jump to the last tab
	case 'End':
		event.preventDefault();
		indexToFocus = lastIndex;
		break;

	// Ignore all other key strokes
	default:
		return;

	}

	const tabToFocus = tabs[indexToFocus];

	if (tabToFocus) {
		tabToFocus.focus();
	}

};

/**
 * Renders a tab list wrapper.
 *
 * Should be used with the TabButton component. Expected key down behavior
 * will be added when TabButton children are used.
 *
 * @param {Object} props  The component props.
 *
 * @returns {React.ReactElement}  The TabList component.
 */
const A11yTabList = (props) => {

	const { children, vertical, ...restProps } = props;

	const handleOnKeyDown = (event, index) => {

		const tabs = document.querySelectorAll(`#${restProps.id} [role="tab"]`);

		onKeyDown(event, tabs, index, vertical);

	};

	return (
		<div
			{...restProps}
			role="tablist">
			{React.Children.map(children, (child, index) => (
				child && React.cloneElement(child, {
					onKeyDown: (event) => handleOnKeyDown(event, index),
				})
			))}
		</div>
	);

};

A11yTabList.propTypes = {

	/**
	 * The unique ID for the tab list instance.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * Optional class name.
	 */
	className: PropTypes.string,

	/**
	 * Accessible label for the tab list.
	 */
	['aria-label']: PropTypes.string.isRequired,

	/**
	 * Flag indicating if the tab list is vertically oriented.
	 */
	vertical: PropTypes.bool,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * Renders a button with a "tab" role.
 *
 * Other attributes will be set based on whether the
 * tab is active or not.
 *
 * @param {Object} props  The component props.
 *
 * @returns {React.ReactElement}  The TabButton component.
 */
const A11yTabButton = (props) => {

	const { active, ...restProps } = props;

	let className = '';

	if (restProps.className) {
		className += restProps.className;
	}

	if (active) {
		className += ' active';
	}

	return (
		<Button
			{...restProps}
			className={className}
			role="tab"
			aria-selected={active}
			tabIndex={active ? '0' : '-1'} />
	);

};

A11yTabButton.propTypes = {

	/**
	 * Flag indicating whether the tab is active.
	 */
	active: PropTypes.bool.isRequired,

	/**
	 * The unique ID of the tab panel that this tab controls.
	 */
	['aria-controls']: PropTypes.string.isRequired,

};

/**
 * Renders a div element with a "tabpanel" role.
 *
 * A unique ID must be specified and match the "aria-controls" attribute
 * for the corresponding TabButton component.
 *
 * @param {Object} props  The component props.
 *
 * @returns {React.ReactElement}  The TabPanel component.
 */
const A11yTabPanel = (props) => {

	const { children, ...restProps } = props;

	return (
		<div
			{...restProps}
			role="tabpanel">
			{children}
		</div>
	);

};

A11yTabPanel.propTypes = {

	/**
	 * The unique ID for the tab panel instance.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * Optional class name.
	 */
	className: PropTypes.string,

	/**
	 * The component children.
	 */
	children: PropTypes.node,

};

export {
	A11yTabList,
	A11yTabButton,
	A11yTabPanel,
	onKeyDown,
};
